/** Why a member was sent to the workspace-access page (picks its copy). */
export type WorkspaceAccessReason = 'trial_expired';

/**
 * Centralized application route definitions
 *
 * This object holds all the main URL patterns used throughout the app.
 * Each entry is a function or string literal that returns a route path.
 */
export const APP_ROUTES = {
  // General Routes
  USER_ONBOARDING_SUCCESS: '/app/onboarding/success',
  USER_DASHBOARD: '/app/user',
  ACCEPT_INVITATION: '/auth/accept-invitation',
  CREATE_ORG: '/app/org/create',
  /**
   * "Contact support to get your workspace set up" — enterprise-only onboarding.
   * `orgId`: the user's org exists but isn't active; `shop`: a Shopify store
   * waiting to be linked once the workspace is set up; `reason`: why an existing
   * org was locked (e.g. its trial ended).
   */
  WORKSPACE_ACCESS: ({
    orgId,
    shop,
    reason
  }: { orgId?: string; shop?: string; reason?: WorkspaceAccessReason } = {}) => {
    const query = new URLSearchParams({
      ...(orgId ? { orgId } : {}),
      ...(shop ? { shop } : {}),
      ...(reason ? { reason } : {})
    }).toString();
    return query ? `/app/access?${query}` : '/app/access';
  },
  /** Sales/ops-only enterprise org provisioning (claim-gated). */
  ADMIN_ENTERPRISE_ONBOARDING: '/app/admin/enterprise-onboarding',
  MANAGE_PROFILE: '/app/user/manage-profile',
  BRAND_DNA: '/app/org/brand-dna',
  ORG_SETUP_SUCCESS: '/app/org/setup/success',
  SELECT_ORG_GOAL: '/app/org/setup/goal',
  SELECT_PRODUCT: '/app/org/setup/product',
  CONNECT_MAGENTO: '/app/org/connect/magento',
  TIKTOK_LOGIN_CALLBACK: '/org/connect/tiktok-login',
  CONNECT_GOOGLE_ADS: '/app/org/connect/google-ads',
  CONNECT_GOOGLE_ANALYTICS: '/app/org/connect/google-analytics',
  CONNECT_GOOGLE_SEARCH_CONSOLE: '/app/org/connect/google-search-console',
  CONNECT_GOOGLE_MY_BUSINESS: '/app/org/connect/google-my-business',
  CONNECT_MIXPANEL: '/app/org/connect/mixpanel-mcp',
  CONNECT_GMC: '/app/org/connect/gmc',
  CONNECT_SHOPIFY: '/app/org/connect/shopify/install',
  CONNECT_FYND_INSTALL: '/app/org/connect/fynd/install',
  CONNECT_FYND_INSTALL_PREPARE: '/app/org/connect/fynd/install/prepare',
  COMPLETE_SHOPIFY_CONNECT: '/app/org/connect/shopify',
  ORG_DASHBOARD: (orgId: string) => `/app/org/${orgId}/chat`,
  INTEGRATIONS: (orgId: string) => `/app/org/${orgId}/integrations`,
  ORG_SETTINGS: (orgId: string) => `/app/org/${orgId}/settings`,
  BRAND_PROFILE_SETTINGS: (orgId: string) => `/app/org/${orgId}/settings/brand-profile`,
  MEMBERS_SETTINGS: (orgId: string) => `/app/org/${orgId}/settings/members`,
  PLANS_BILLING_SETTINGS: (orgId: string) => `/app/org/${orgId}/settings/plans-billing`,
  COMMANDS_SETTINGS: (orgId: string) => `/app/org/${orgId}/settings/commands`,
  PERSONALIZATION_SETTINGS: (orgId: string) => `/app/org/${orgId}/settings/personalization`,
  STRIQUE_UPGRADE: (orgId: string) => `/app/org/upgrade?orgId=${orgId}`,
  STRIQUE_PRICING: (orgId: string) => `/app/org/pricing?orgId=${orgId}`,

  // Catalog Routes
  CATALOG: (orgId: string) => `/app/org/${orgId}/catalog`,
  CATALOG_ONBOARDING: (orgId: string) => `/app/org/${orgId}/catalog/onboarding`,
  CATALOG_ONBOARDING_CHOOSE_ACCOUNT: (orgId: string) =>
    `/app/org/${orgId}/catalog/onboarding/choose-account`,
  CATALOG_ONBOARDING_CONNECT_STORE: (orgId: string) =>
    `/app/org/${orgId}/catalog/onboarding/connect-store`,
  META_CATALOG_ONBOARDING: (orgId: string) => `/app/org/${orgId}/catalog/onboarding/meta`,
  GMC_CATALOG_ONBOARDING: (orgId: string) => `/app/org/${orgId}/catalog/onboarding/gmc`,
  CATALOG_PRODUCTS: (orgId: string, productId: string) =>
    `/app/org/${orgId}/catalog/products/${productId}`,
  CATALOG_INTEGRATION_DETAILS: (orgId: string, platform: 'gmc' | 'meta-catalog') =>
    `/app/org/${orgId}/integrations/${platform}`,
  CATALOG_SETTINGS: (orgId: string) => `/app/org/${orgId}/catalog/settings`,
  SHOPIFY_SYNC_SETTINGS: (orgId: string) => `/app/org/${orgId}/catalog/settings/shopify`,
  FACEBOOK_SETTINGS: (orgId: string) => `/app/org/${orgId}/catalog/settings/facebook`,
  GOOGLE_SYNC_SETTINGS: (orgId: string) => `/app/org/${orgId}/catalog/settings/google`,
  XML_FEED_SETTINGS: (orgId: string) => `/app/org/${orgId}/catalog/settings/xml`,
  AUTOMATED_RULES_FEED: (orgId: string) => `/app/org/${orgId}/catalog/settings/automated-rules`,
  PRODUCT_SETS: (orgId: string) => `/app/org/${orgId}/catalog/settings/product-sets`,
  ACTIVITY_LOGS: (orgId: string) => `/app/org/${orgId}/catalog/settings/activity-logs`,
  FEED_QUALITY: (orgId: string) => `/app/org/${orgId}/catalog/feed-quality`,
  META_FEED_QUALITY: (orgId: string) => `/app/org/${orgId}/catalog/feed-quality/meta-catalog`,
  GMC_FEED_QUALITY: (orgId: string) => `/app/org/${orgId}/catalog/feed-quality/gmc`,

  //Chat App Routes
  CHAT_APP: (orgId: string) => `/app/org/${orgId}/chat`,
  CHAT_ID_PAGE: (orgId: string, convId: string) => `/app/org/${orgId}/chat?id=${convId}`,

  // Activity Logs
  // ACTIVITY_LOGS: (orgId: string) => `/app/org/${orgId}/activity-logs`,

  // Zapier MCP integration detail page
  ZAPIER_INTEGRATION: (orgId: string) => `/app/org/${orgId}/integrations/zapier`
} as const;
